import { InvestmentGoalResponse, SavingsGoalResponse } from "../../../services";
import {
  valuationAmountText,
  valuationMessageFor,
} from "../../../services/valuation";
import {
  DateFormat,
  formatCompactNumber,
  formatDate,
  predictedDateFromMonths,
} from "../../../utils";
import {
  MISSING_VALUE,
  OUTCOME_LABEL,
  OUTCOME_TONE,
} from "../../Settings/GoalsSection/constants";
import {
  PACE_BAR_COLOR,
  REACHED_BAR_COLOR,
  savingsGoalStatusOf,
} from "../../Settings/GoalsSection/goalStatus";
import { hasCurrentValuation } from "../../Settings/GoalsSection/investmentGoalStatus";
import { PlanGoal } from "./types";

const FULL_PERCENT = 100;
const PRIMARY_BAR_COLOR = "var(--color-primary)";

export const compactEuro = (value: number) => `€${formatCompactNumber(value)}`;

const percentOf = (amount: number, target: number) =>
  target > 0 ? Math.min((amount / target) * FULL_PERCENT, FULL_PERCENT) : 0;

export const savingsPlanGoal = (
  goal: SavingsGoalResponse,
  canRollForward: boolean,
): PlanGoal => {
  const { progress } = goal;
  const target = compactEuro(goal.targetAmount);

  if (goal.closedAt !== null && goal.outcome !== null) {
    const closedAmount = goal.closedAmount ?? progress.currentAmount;
    const isReached = goal.outcome === "REACHED";
    return {
      name: goal.name,
      amountText: `${compactEuro(closedAmount)} of ${target}`,
      progressPercent: percentOf(closedAmount, goal.targetAmount),
      statusLabel: OUTCOME_LABEL[goal.outcome],
      statusTone: OUTCOME_TONE[goal.outcome],
      barColor: isReached ? REACHED_BAR_COLOR : PACE_BAR_COLOR.BEHIND,
      message: null,
      rollForwardGoalId: canRollForward ? goal.id : null,
    };
  }

  const status = savingsGoalStatusOf(progress);
  return {
    name: goal.name,
    amountText: `${compactEuro(progress.currentAmount)} of ${target}`,
    progressPercent: progress.progressPercent,
    statusLabel: status.label,
    statusTone: status.tone,
    barColor: status.barColor,
    message: null,
    rollForwardGoalId: null,
  };
};

export const investmentPlanGoal = (goal: InvestmentGoalResponse): PlanGoal => {
  const { projection } = goal;
  const target = compactEuro(goal.targetAmount);

  if (goal.reachedAt !== null) {
    return {
      name: goal.name,
      amountText: `${compactEuro(goal.reachedAmount ?? goal.targetAmount)} of ${target}`,
      progressPercent: FULL_PERCENT,
      statusLabel: "Target reached",
      statusTone: "success",
      barColor: REACHED_BAR_COLOR,
      message: null,
      rollForwardGoalId: null,
    };
  }

  if (goal.archivedAt !== null) {
    const { archivedAmount } = goal;
    const hasArchivedAmount = archivedAmount !== null;
    const archivedText = hasArchivedAmount
      ? compactEuro(archivedAmount)
      : MISSING_VALUE;
    return {
      name: goal.name,
      amountText: `${archivedText} of ${target}`,
      progressPercent: hasArchivedAmount
        ? percentOf(archivedAmount, goal.targetAmount)
        : null,
      statusLabel: "Archived",
      statusTone: "info",
      barColor: PRIMARY_BAR_COLOR,
      message: null,
      rollForwardGoalId: null,
    };
  }

  if (!hasCurrentValuation(goal)) {
    return {
      name: goal.name,
      amountText: `${valuationAmountText({
        value: projection.currentAmount,
        valuation: projection.valuation,
      })} of ${target}`,
      progressPercent: null,
      statusLabel: null,
      statusTone: "warning",
      barColor: PRIMARY_BAR_COLOR,
      message: valuationMessageFor(projection.valuation),
      rollForwardGoalId: null,
    };
  }

  const amountText = `${compactEuro(projection.currentAmount ?? 0)} of ${target}`;
  const progressPercent = projection.progressPercent ?? 0;
  if (projection.reached) {
    return {
      name: goal.name,
      amountText,
      progressPercent,
      statusLabel: "Target reached",
      statusTone: "success",
      barColor: REACHED_BAR_COLOR,
      message: null,
      rollForwardGoalId: null,
    };
  }

  const predictedDate = predictedDateFromMonths(projection.monthsToTarget);
  const hasProjection = predictedDate !== null;
  return {
    name: goal.name,
    amountText,
    progressPercent,
    statusLabel: hasProjection
      ? `Projected ${formatDate(predictedDate, DateFormat.MY_SPACE)}`
      : "Not reachable",
    statusTone: hasProjection ? "info" : "warning",
    barColor: PRIMARY_BAR_COLOR,
    message: null,
    rollForwardGoalId: null,
  };
};
